import axios from '../axiosInstance';
import { createCrudApi } from '../createCrudApi';
import { PaginatedResponse } from '../types';

export type ProtocolsGetParams = {
  limit?: number;
  offset?: number;
  ordering?: string;
  search?: string;
  deceased?: number;
  applicant?: number;
  classification?: number;
  is_deactivated?: boolean;
  is_locked?: boolean;
  is_not_autopsied?: boolean;
  examination_type?: number;
  protocol_year?: number;
};

export type DeceasedTableParams = {
  deceased_name?: string;
  birth_id?: string;
  protocol_number?: number;
  protocol_year?: number;
  doctor_id?: number;
  doctor2_id?: number;
  is_locked?: boolean;
  limit?: number;
  offset?: number;
  ordering?: string;
};

export type DeceasedTableItem = {
  id: number;
  protocol_year: number;
  protocol_number: number;
  protocol_display: string;
  full_name: string;
  title_before: string;
  title_after: string;
  birth_id: string;
  insurance_company_name: string;
  date_of_birth: string;
  date_of_death: string;
  age_years: number;
  residence_address: string;
  contact: string;
  pathologist1_name: string;
  pathologist2_name: string;
  has_k_davka: boolean;
  sent_to_nrpatv: boolean;
  sent_to_uzis_at?: string | null;
  has_active_request: boolean;
  note: string;
  is_locked?: boolean;
  is_deactivated?: boolean;
};

export type ProtocolList = {
  id: number;
  protocol_year: number;
  protocol_number: number;
  deceased: number;
  deceased_display: string;
  date_of_death: string | null;
  time_of_death: string | null;
  autopsy_date: string | null;
  autopsy_time: string | null;
  examination_type_name: string;
  applicant_name: string;
  classification_name: string;
  pathologist1_name: string;
  pathologist2_name: string;
  is_not_autopsied: boolean;
  is_deactivated: boolean;
  is_locked: boolean;
  is_sent_to_uzis: boolean;
};

export type Protocol = {
  id: number;
  protocol_year: number;
  protocol_number: number;
  deceased: number;
  deceased_display: string;
  is_not_autopsied: boolean;
  examination_type: number | null;
  examination_type_name: string;
  confidentiality_waiver: boolean | null;
  sent_side_effects_report: boolean | null;
  sent_to_NOR: boolean | null;
  sent_to_TBC: boolean | null;
  is_interrupted: boolean | null;
  forensic_consultation: boolean;
  autopsy_date: string | null;
  autopsy_time: string | null;
  applicant: number | null;
  applicant_name: string;
  blocks: string;
  slides: string;
  date_of_death: string | null;
  time_of_death: string | null;
  death_time_unknown: boolean;
  death_date_unknown: boolean;
  place_of_death: string;
  is_death_time_estimate: boolean;
  place_of_death_ruian: number | null;
  is_stillborn: boolean;
  drug_related_death: boolean;
  precious_metal_implants: boolean;
  precious_metal_implants_note: string;
  cause_of_death_autopsy: string;
  examining_physician: number | null;
  examining_physician_name: string;
  examining_physician_text: string;
  pathologist1: number | null;
  pathologist1_name: string;
  pathologist2: number | null;
  pathologist2_name: string;
  created_by: number | null;
  created_by_name: string;
  lab_technician: number | null;
  lab_technician_name: string;
  attendees: string | null;
  classification: number | null;
  classification_name: string;
  autopsy_diagnosis: string;
  clinical_dg1: number | null;
  clinical_dg2: number | null;
  clinical_dg3: number | null;
  autopsy_dg1: number | null;
  autopsy_dg2: number | null;
  autopsy_dg3: number | null;
  mechanism_dg1: number | null;
  mechanism_dg2: number | null;
  mechanism_dg3: number | null;
  mechanism_dg4: number | null;
  mechanism_dg5: number | null;
  clinical_dg1_code: string;
  clinical_dg2_code: string;
  clinical_dg3_code: string;
  autopsy_dg1_code: string;
  autopsy_dg2_code: string;
  autopsy_dg3_code: string;
  mechanism_dg1_code: string;
  mechanism_dg2_code: string;
  mechanism_dg3_code: string;
  mechanism_dg4_code: string;
  mechanism_dg5_code: string;
  note: string;
  is_deactivated: boolean;
  is_locked: boolean;
  last_printed_at: string | null;
  sent_to_uzis_at: string | null;
  uzis_identifier: string;
  is_sent_to_uzis: boolean;
  locked_date: string | null;
};

export type ProtocolFormData = Partial<
  Omit<
    Protocol,
    | 'id'
    | 'protocol_number'
    | 'deceased_display'
    | 'examination_type_name'
    | 'applicant_name'
    | 'examining_physician_name'
    | 'pathologist1_name'
    | 'pathologist2_name'
    | 'created_by_name'
    | 'lab_technician_name'
    | 'classification_name'
    | 'clinical_dg1_code'
    | 'clinical_dg2_code'
    | 'clinical_dg3_code'
    | 'autopsy_dg1_code'
    | 'autopsy_dg2_code'
    | 'autopsy_dg3_code'
    | 'mechanism_dg1_code'
    | 'mechanism_dg2_code'
    | 'mechanism_dg3_code'
    | 'mechanism_dg4_code'
    | 'mechanism_dg5_code'
    | 'last_printed_at'
    | 'sent_to_uzis_at'
    | 'uzis_identifier'
    | 'is_sent_to_uzis'
  >
>;

export type ProtocolSequence = {
  protocol_year: number;
  next_number: number;
};

export type ActiveProtocolSequence = {
  active_year: number;
  next_number: number;
};

const ENDPOINT = '/v1/autopsy-protocols/';
const SEQUENCE_ENDPOINT = '/v1/autopsy-protocol-sequence/';

export const ProtocolsApi = () => ({
  ...createCrudApi<Protocol, ProtocolFormData, ProtocolsGetParams>(ENDPOINT),

  deactivate: (id: number) => axios.post<Protocol>(`${ENDPOINT}${id}/deactivate/`),

  activate: (id: number) => axios.post<Protocol>(`${ENDPOINT}${id}/activate/`),

  lock: (id: number) => axios.post<Protocol>(`${ENDPOINT}${id}/lock/`),

  unlock: (id: number) => axios.post<Protocol>(`${ENDPOINT}${id}/unlock/`),

  print: (id: number) => axios.get<Blob>(`${ENDPOINT}${id}/print/`, { responseType: 'blob' }),

  sendToUzis: (id: number) => axios.post<Protocol>(`${ENDPOINT}${id}/send-to-uzis/`),

  getProtocolYears: () => axios.get<any>(`${ENDPOINT}protocol-years/`),

  getDeceasedTable: (params?: DeceasedTableParams) =>
    axios.get<PaginatedResponse<DeceasedTableItem>>(`${ENDPOINT}deceased-table/`, { params }),

  getProtocolSequences: () => axios.get<ProtocolSequence[]>(`${SEQUENCE_ENDPOINT}`),

  getActiveProtocolSequence: () => axios.get<ActiveProtocolSequence>(`${SEQUENCE_ENDPOINT}active/`),

  activateProtocolSequence: (year: number, startNumber: number) =>
    axios.patch(`${SEQUENCE_ENDPOINT}active/`, { active_year: year, next_number: startNumber })
});
